// Flatrock — Product spin.
// Draws a preloaded turntable frame sequence into a canvas and drives the frame
// position from either scroll through a pinned track or a continuous
// auto-rotation. Adjacent frames are cross-dissolved on the fractional part of
// that position, so smoothness is independent of how many frames exist and how
// slowly it turns. In scroll mode callouts reveal as the rotation passes their
// `data-at` point; in auto mode they stay up permanently — see init().
//
// Canvas rather than 24+ stacked <img> elements: one compositing layer, no
// flash of unstyled frame swaps, and the frame index is a single integer to
// animate. Frames are decoded up front so scrubbing never waits on the network.
//
// Giant layout (data-layout="giant"): the callouts become feature tiles plus a
// "now showing" readout, and one feature is active at a time. That needs the
// rotation position in every mode — including video, which otherwise runs
// without JS involvement — so see syncFeatures() and watchVideo().

const REDUCED = matchMedia('(prefers-reduced-motion: reduce)');
const MOBILE = matchMedia('(max-width: 749px)');

class ProductSpin {
  constructor(track) {
    this.track = track;
    this.section = track.closest('[data-section-id]');
    this.canvas = track.querySelector('[data-flrc-spin-canvas]');
    this.viewport = track.querySelector('.flrc-spin__viewport');
    this.stage = track.querySelector('.flrc-spin__stage');
    this.pin = track.querySelector('.flrc-spin__pin');
    this.readout = track.querySelector('[data-flrc-spin-readout]');
    this.callouts = [...track.querySelectorAll('[data-flrc-spin-callout]')];
    this.mobileNotes = [...track.querySelectorAll('[data-flrc-spin-mobile-note]')];

    // Giant layout parts. Empty in the classic layout, which makes every
    // feature-sync call below a no-op there.
    this.features = [...track.querySelectorAll('[data-flrc-spin-feature]')];
    this.dots = [...track.querySelectorAll('[data-flrc-spin-dot]')];
    this.now = track.querySelector('[data-flrc-spin-now]');
    this.activeFeature = this.features.length ? 0 : -1;

    this.mode = track.dataset.mode === 'auto' ? 'auto' : 'scroll';
    this.spins = Math.max(1, parseInt(track.dataset.spins, 10) || 1);
    this.dragEnabled = track.dataset.drag === 'true';
    // Crops the empty margin baked into the source frames so the garment can
    // fill more of the stage without the card growing to match.
    this.zoom = parseFloat(track.dataset.zoom) || 1;
    this.secondsPerTurn = Math.max(1, parseFloat(track.dataset.secondsPerTurn) || 14);

    // Cross-dissolving adjacent frames is what lets the rotation be slow AND
    // smooth at once. Snapping to the nearest of 24 frames over a 36s turn is
    // 0.67fps of discrete steps, and slowing it down only makes each step sit
    // longer — blending decouples smoothness from frame count entirely. At 15°
    // between frames the two poses are close enough that it reads as motion
    // blur rather than a double exposure.
    this.blend = !REDUCED.matches;

    this.frames = [];
    this.images = [];
    this.index = 0;
    this.framePos = 0;
    this.dragging = false;
    this.progress = 0;
    this.ticking = false;
    this.visible = false;
    this.destroyed = false;

    this.onScroll = this.onScroll.bind(this);
    this.onResize = this.onResize.bind(this);
    this.tick = this.tick.bind(this);

    this.init();
  }

  // Which path won, on the track as data-flrc-spin-state, so a "the spin
  // doesn't show" report can be answered from one look at the element:
  // poster (server still, nothing better yet) / video-pending / video /
  // frames-loading / frames / static.
  setState(state) {
    this.track.dataset.flrcSpinState = state;
  }

  init() {
    this.bindFeatures();

    // Video wins when it can play: it is hardware-decoded, holds a couple of
    // frames in memory instead of the whole sequence, and needs no blending, so
    // there is no cross-dissolve ghosting. Returns false if there's no usable
    // source for this engine, in which case we fall through to the frames.
    this.video = this.track.querySelector('[data-flrc-spin-video]');
    if (this.video && this.initVideo()) return;

    const data = this.section && this.section.querySelector('[data-flrc-spin-frames]');
    if (!data || !this.canvas) return;

    try {
      this.frames = JSON.parse(data.textContent).filter(Boolean);
    } catch (err) {
      return;
    }
    if (!this.frames.length) return;

    this.ctx = this.canvas.getContext('2d', { alpha: true });
    this.track.style.setProperty('--flrc-spin-frames', this.frames.length);

    // A single-frame sequence has nothing to scrub; keep the still and the
    // callouts, drop the pin so the section doesn't eat 300vh for no reason.
    if (this.frames.length === 1 || REDUCED.matches) {
      this.track.classList.add('flrc-spin--static');
      this.callouts.forEach((el) => el.classList.add('is-active'));
      this.mobileNotes.forEach((el) => el.classList.add('is-active'));
    }

    // Auto-rotate loops forever, so a progress-gated reveal would blink every
    // callout off at the wrap and replay the whole sequence once per turn. The
    // notes are permanent annotations here instead of a scroll payoff.
    if (this.mode === 'auto' && !this.track.classList.contains('flrc-spin--static')) {
      this.persistentCallouts = true;
      this.track.classList.add('flrc-spin--notes-persistent');
      this.callouts.forEach((el) => el.classList.add('is-active'));
      this.mobileNotes.forEach((el) => el.classList.add('is-active'));
    }

    this.setState('frames-loading');
    this.load().then(() => {
      if (this.destroyed) return;
      this.resize();
      this.draw(0);
      this.track.classList.add('is-ready');
      this.setState(this.track.classList.contains('flrc-spin--static') ? 'static' : 'frames');
      this.bind();
    });
  }

  initVideo() {
    // Alpha support cannot be feature-detected. Safari plays VP9-in-WebM but
    // silently drops the alpha channel and paints an opaque box, so canPlayType
    // is no help — it says "maybe" either way. Engine sniffing is the reliable
    // split: only WebKit on Apple reports an Apple vendor; Chrome says "Google
    // Inc." even on macOS.
    const webkit = /Apple/i.test(navigator.vendor || '');
    const src = webkit ? this.video.dataset.srcHevc : this.video.dataset.srcWebm;
    if (!src) return false;
    // Reduced motion never plays the video, and a paused video that has only
    // loaded metadata paints nothing on iOS. The canvas path holds frame one.
    if (REDUCED.matches) {
      this.video.remove();
      this.video = null;
      return false;
    }

    this.mode = 'video';
    this.persistentCallouts = true;
    this.track.style.setProperty('--flrc-spin-zoom', this.zoom);
    this.track.classList.add('flrc-spin--video', 'flrc-spin--notes-persistent');
    this.callouts.forEach((el) => el.classList.add('is-active'));
    this.mobileNotes.forEach((el) => el.classList.add('is-active'));

    this.video.addEventListener(
      'loadedmetadata',
      () => {
        if (!this.video) return;
        const { videoWidth: w, videoHeight: h } = this.video;
        if (w && h) this.viewport.style.setProperty('--flrc-spin-aspect', `${w} / ${h}`);
        this.publishArtRect(this.viewport.getBoundingClientRect());
      },
      { once: true }
    );

    // Ready means frames are moving, not "metadata arrived". iOS WebViews reach
    // loadedmetadata and then paint nothing for a paused or refused video, and
    // ready is what fades the server-rendered still out — so it waits for the
    // clock to actually advance.
    const onTime = () => {
      if (!this.video || this.video.currentTime <= 0) return;
      this.video.removeEventListener('timeupdate', onTime);
      this.videoPlaying = true;
      this.clearWatchdog();
      this.track.classList.add('is-ready');
      this.setState('video');
    };
    this.video.addEventListener('timeupdate', onTime);

    // A bad or unsupported encode must not leave an empty box: tear the video
    // out and re-enter init(), which now finds no video element and builds the
    // canvas sequence instead.
    this.video.addEventListener('error', () => this.dropVideo(), { once: true });

    this.video.src = src;
    this.setState('video-pending');
    this.watchVideo();

    // Play only while on screen. iOS Low Power Mode (and some data-saver
    // settings) refuse autoplay with NotAllowedError; a paused, poster-less
    // video paints nothing there, so fall back to the canvas frames.
    this.observer = new IntersectionObserver(
      (entries) => {
        if (!this.video) return;
        if (entries[0].isIntersecting) {
          this.armWatchdog();
          const played = this.video.play();
          // Old engines return undefined rather than a promise.
          if (played && played.catch) {
            played.catch((err) => {
              // AbortError is our own pause() landing while play() was still
              // pending (scrolled away); the next intersection retries it.
              // Anything else — NotAllowedError (autoplay refused: in-app
              // WebViews, Low Power Mode), NotSupportedError, unknown — means
              // this video will not play here, so use the frames.
              if (err && err.name === 'AbortError') return;
              this.dropVideo();
            });
          }
        } else {
          this.clearWatchdog();
          this.video.pause();
        }
      },
      { threshold: 0.1 }
    );
    this.observer.observe(this.track);

    addEventListener('resize', this.onResize, { passive: true });
    return true;
  }

  // A play() that never settles, or a decoder that never produces a frame,
  // fires no event at all. So, while the section is on screen: once the video
  // has data but is still not moving after a few seconds, autoplay was
  // silently refused; if it has no data at all, keep waiting up to a limit
  // (a slow connection is not a failure). Either way, the frames take over.
  armWatchdog() {
    if (this.videoPlaying || this.watchdog) return;
    let waited = 0;
    const STEP = 3000;
    const LIMIT = 15000;
    const check = () => {
      this.watchdog = null;
      if (!this.video || this.videoPlaying) return;
      waited += STEP;
      if (this.video.readyState >= 2 || waited >= LIMIT) {
        this.dropVideo();
        return;
      }
      this.watchdog = setTimeout(check, STEP);
    };
    this.watchdog = setTimeout(check, STEP);
  }

  clearWatchdog() {
    clearTimeout(this.watchdog);
    this.watchdog = null;
  }

  // A bad or unsupported encode, or a refused autoplay, must not leave an
  // empty box: tear the video out and re-enter init(), which now finds no
  // video element and builds the canvas sequence instead.
  dropVideo() {
    if (!this.video) return;
    this.clearWatchdog();
    if (this.observer) this.observer.disconnect();
    this.observer = null;
    if (this.rafVideo) cancelAnimationFrame(this.rafVideo);
    this.rafVideo = null;
    this.track.classList.remove('flrc-spin--video', 'is-ready');
    this.video.pause();
    this.video.remove();
    this.video = null;
    this.mode = this.track.dataset.mode === 'auto' ? 'auto' : 'scroll';
    this.init();
  }

  async load() {
    const decode = (src) =>
      new Promise((resolve) => {
        const img = new Image();
        img.decoding = 'async';
        img.onload = () => resolve(img);
        img.onerror = () => resolve(null);
        img.src = src;
      });

    // First frame first so something paints as early as possible, then the rest
    // four at a time to avoid saturating the connection on mobile.
    this.images[0] = await decode(this.frames[0]);
    if (this.images[0]) {
      // Sizing the stage box to the frame's ratio means capping its width caps the
      // garment, which is what keeps the note gutters predictable.
      const { naturalWidth: w, naturalHeight: h } = this.images[0];
      if (w && h) this.viewport.style.setProperty('--flrc-spin-aspect', `${w} / ${h}`);
      this.resize();
      this.draw(0);
    }

    const rest = this.frames.slice(1);
    const BATCH = 4;
    for (let i = 0; i < rest.length; i += BATCH) {
      if (this.destroyed) return;
      const batch = await Promise.all(rest.slice(i, i + BATCH).map(decode));
      batch.forEach((img, n) => {
        this.images[i + n + 1] = img;
      });
    }
  }

  bind() {
    if (this.track.classList.contains('flrc-spin--static')) {
      this.observeStatic();
      return;
    }

    addEventListener('resize', this.onResize, { passive: true });

    if (this.mode === 'auto') {
      this.observer = new IntersectionObserver(
        (entries) => {
          this.visible = entries[0].isIntersecting;
          if (this.visible) this.autoStart();
        },
        { threshold: 0.2 }
      );
      this.observer.observe(this.track);
    } else {
      addEventListener('scroll', this.onScroll, { passive: true });
      this.onScroll();
    }

    if (this.dragEnabled) this.bindDrag();
  }

  observeStatic() {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          this.callouts.forEach((el) => el.classList.add('is-active'));
          io.unobserve(entry.target);
        });
      },
      { threshold: 0.2 }
    );
    io.observe(this.track);
  }

  autoStart() {
    if (this.rafAuto) return;
    let last = null;
    let painted = 0;
    // Blending repaints every tick rather than only when the frame index
    // changes, so cap the rate on phones where compositing two full-size images
    // per frame is real work. 30fps is still well past the flicker threshold.
    const minFrameMs = MOBILE.matches ? 1000 / 30 : 0;

    const step = (now) => {
      if (this.destroyed) return;
      if (!this.visible) {
        this.rafAuto = null;
        return;
      }
      if (last === null) last = now;
      const delta = (now - last) / 1000;
      last = now;

      // A drag owns the rotation while it lasts — time keeps passing, but the
      // suit doesn't creep out from under the finger.
      if (!this.dragging) {
        this.progress = (this.progress + delta / this.secondsPerTurn) % 1;
        if (now - painted >= minFrameMs) {
          painted = now;
          this.render(this.progress);
        }
      }

      this.rafAuto = requestAnimationFrame(step);
    };

    this.rafAuto = requestAnimationFrame(step);
  }

  onScroll() {
    if (this.ticking) return;
    this.ticking = true;
    requestAnimationFrame(this.tick);
  }

  tick() {
    this.ticking = false;
    const rect = this.track.getBoundingClientRect();

    // Travel is the distance the track moves while the pin is stuck, which is
    // governed by the pin's own height and its sticky offset — NOT by the
    // viewport. Assuming innerHeight here is what forced the pinned block to be
    // exactly one viewport tall; measuring it lets the section be a short band
    // that still scrubs correctly. Falls back to the old behaviour if the pin
    // is missing.
    const pinHeight = this.pin ? this.pin.offsetHeight : innerHeight;
    const stickyTop = this.pin ? parseFloat(getComputedStyle(this.pin).top) || 0 : 0;
    const travel = rect.height - pinHeight - stickyTop;

    if (travel <= 0) {
      this.render(0);
      return;
    }
    const raw = (stickyTop - rect.top) / travel;
    this.progress = Math.min(1, Math.max(0, raw));
    this.render(this.progress);
  }

  render(progress) {
    const total = this.frames.length;
    const turned = progress * this.spins;
    // Fractional frame position — the part after the decimal is how far between
    // two frames we are, which is exactly the blend weight.
    const pos = ((((turned % 1) + 1) % 1)) * total;

    if (this.blend) {
      this.framePos = pos;
      this.index = Math.floor(pos) % total;
      this.draw(pos);
    } else {
      const index = Math.round(pos) % total;
      if (index !== this.index) {
        this.index = index;
        this.draw(index);
      }
    }

    if (this.readout) {
      const deg = Math.round((pos / total) * 360) % 360;
      this.readout.textContent = `${String(deg).padStart(3, '0')}°`;
    }

    // Published for the scroll-progress rail, which is drawn entirely in CSS.
    this.track.style.setProperty('--flrc-spin-progress', progress);

    this.syncCallouts(progress);
    this.syncFeatures(((turned % 1) + 1) % 1);
  }

  // --- Giant layout: active feature ---------------------------------------

  bindFeatures() {
    if (!this.features.length || this.featuresBound) return;
    this.featuresBound = true;
    this.features.forEach((el, i) => {
      el.addEventListener('click', () => this.seekFeature(i));
    });
  }

  // The feature whose `at` the rotation most recently passed. Before the first
  // one, the turn is still in the last feature's stretch from the previous
  // lap — so it wraps rather than showing nothing.
  featureAt(rotation) {
    const pct = rotation * 100;
    const ats = this.features.map((el) => parseFloat(el.dataset.at) || 0);
    let index = ats.length - 1;
    ats.forEach((at, i) => {
      if (pct >= at) index = i;
    });
    const from = ats[index];
    const to = ats[(index + 1) % ats.length];
    const span = (to - from + 100) % 100 || 100;
    const through = Math.min(1, ((pct - from + 100) % 100) / span);
    return { index, through };
  }

  syncFeatures(rotation) {
    if (!this.features.length) return;
    if (this.holdFeatureUntil && performance.now() < this.holdFeatureUntil) return;
    const { index, through } = this.featureAt(rotation);
    this.features[index].style.setProperty('--flrc-feature-progress', through.toFixed(4));
    if (index === this.activeFeature) return;
    this.setFeature(index);
  }

  setFeature(index) {
    this.activeFeature = index;
    this.features.forEach((el, i) => el.classList.toggle('is-active', i === index));
    this.dots.forEach((el, i) => el.classList.toggle('is-active', i === index));
    if (!this.now) return;

    const el = this.features[index];
    const fill = () => {
      this.now.querySelector('[data-flrc-spin-now-num]').textContent = el.dataset.num;
      this.now.querySelector('[data-flrc-spin-now-label]').textContent = el.dataset.label;
      this.now.querySelector('[data-flrc-spin-now-body]').textContent = el.dataset.body;
      this.now.classList.remove('is-swapping');
    };
    if (REDUCED.matches) return fill();
    this.now.classList.add('is-swapping');
    clearTimeout(this.swapTimer);
    this.swapTimer = setTimeout(fill, 250);
  }

  // Turn the suit to a feature. Lands just past its `at` so rounding can't
  // leave the previous feature active.
  seekFeature(index) {
    const rotation = Math.min(0.999, ((parseFloat(this.features[index].dataset.at) || 0) + 0.5) / 100);

    if (this.mode === 'video' && this.video) {
      const target = rotation * (this.video.duration || 0);
      const { seekable } = this.video;
      // Seeking needs HTTP range support. Shopify's CDN has it; the local
      // `shopify theme dev` proxy doesn't, and assigning currentTime there
      // restarts the loop from zero. When the video can't seek, hold the chosen
      // feature on screen for a moment instead of letting the clock overrule it.
      if (seekable.length && seekable.end(seekable.length - 1) >= target) {
        this.video.currentTime = target;
      } else {
        this.holdFeatureUntil = performance.now() + 5000;
      }
      this.setFeature(index);
      return;
    }
    // Scroll mode belongs to the scrollbar; a jump would fight it on the next
    // scroll event. Just show the feature.
    if (this.mode === 'scroll' || !this.images.length) {
      this.setFeature(index);
      return;
    }
    this.progress = rotation / this.spins;
    this.render(this.progress);
  }

  // Video plays on its own, so poll its clock while it's playing. One lap of
  // the loop is one turn.
  watchVideo() {
    if (!this.features.length) return;
    const step = () => {
      if (this.destroyed || !this.video || this.video.paused) {
        this.rafVideo = null;
        return;
      }
      if (this.video && this.video.duration) this.syncFeatures(this.video.currentTime / this.video.duration);
      this.rafVideo = requestAnimationFrame(step);
    };
    this.video.addEventListener('play', () => {
      if (!this.rafVideo) this.rafVideo = requestAnimationFrame(step);
    });
  }

  syncCallouts(progress) {
    if (this.persistentCallouts) return;
    const pct = progress * 100;
    let activeIndex = -1;

    this.callouts.forEach((el, i) => {
      const at = parseFloat(el.dataset.at) || 0;
      const on = pct >= at;
      el.classList.toggle('is-active', on);
      if (on) activeIndex = i;
    });

    // On mobile there is no room beside the suit, so notes take over the lede's
    // slot in the left column, one at a time.
    if (this.mobileNotes.length) {
      this.mobileNotes.forEach((el, i) => {
        el.classList.toggle('is-active', i === activeIndex);
      });
      this.track.classList.toggle('has-active-note', activeIndex >= 0);
    }
  }

  bindDrag() {
    let startX = 0;
    let startProgress = 0;
    let dragging = false;

    const down = (event) => {
      dragging = true;
      this.dragging = true;
      startX = event.clientX;
      startProgress = this.progress;
      this.viewport.classList.add('is-dragging');
      if (this.viewport.setPointerCapture) this.viewport.setPointerCapture(event.pointerId);
    };

    const move = (event) => {
      if (!dragging) return;
      const width = this.viewport.clientWidth || 1;
      const delta = (event.clientX - startX) / width;
      let next = (startProgress + delta) % 1;
      if (next < 0) next += 1;
      this.progress = next;
      this.render(next);
      event.preventDefault();
    };

    const up = (event) => {
      if (!dragging) return;
      dragging = false;
      this.dragging = false;
      this.viewport.classList.remove('is-dragging');
      if (this.viewport.releasePointerCapture) this.viewport.releasePointerCapture(event.pointerId);
      // Scroll mode owns progress; hand it back so the next scroll doesn't jump.
      if (this.mode === 'scroll') this.onScroll();
    };

    this.viewport.addEventListener('pointerdown', down);
    this.viewport.addEventListener('pointermove', move);
    this.viewport.addEventListener('pointerup', up);
    this.viewport.addEventListener('pointercancel', up);
  }

  onResize() {
    clearTimeout(this.resizeTimer);
    this.resizeTimer = setTimeout(() => {
      if (this.mode === 'video') {
        this.publishArtRect(this.viewport.getBoundingClientRect());
        return;
      }
      this.resize();
      this.draw(this.blend ? this.framePos : this.index);
    }, 120);
  }

  resize() {
    const rect = this.viewport.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    const dpr = Math.min(2, devicePixelRatio || 1);
    this.canvas.width = Math.round(rect.width * dpr);
    this.canvas.height = Math.round(rect.height * dpr);
    this.canvas.style.width = '100%';
    this.canvas.style.height = '100%';
    this.publishArtRect(rect);
  }

  // The garment occupies only part of the canvas box (contain fit, and the frame
  // itself carries a little padding). Callout anchors are percentages of the
  // garment, not of the box, so its rect goes to CSS for them to position from.
  // Intrinsic size of whatever is being shown — video frame or still. The
  // callout anchors are percentages of this, so both paths must answer.
  sourceSize() {
    if (this.video && this.video.videoWidth) {
      return { w: this.video.videoWidth, h: this.video.videoHeight };
    }
    const img = this.images.find(Boolean);
    return img ? { w: img.naturalWidth, h: img.naturalHeight } : null;
  }

  publishArtRect(viewportRect) {
    const src = this.sourceSize();
    if (!src || !this.stage) return;

    const stage = this.stage.getBoundingClientRect();
    // Must carry the same zoom as draw() (or the video's CSS transform), or the
    // callout anchors drift off the garment they point at.
    const scale = Math.min(viewportRect.width / src.w, viewportRect.height / src.h) * this.zoom;
    const w = src.w * scale;
    const h = src.h * scale;

    const set = (name, value) => this.stage.style.setProperty(name, `${value}px`);
    set('--flrc-art-x', viewportRect.left - stage.left + (viewportRect.width - w) / 2);
    set('--flrc-art-y', viewportRect.top - stage.top + (viewportRect.height - h) / 2);
    set('--flrc-art-w', w);
    set('--flrc-art-h', h);
  }

  frameAt(index) {
    const total = this.frames.length;
    if (!total) return null;
    return this.images[((index % total) + total) % total] || null;
  }

  // Accepts a fractional position. The whole part picks the frame, the fraction
  // dissolves into the next one — that dissolve is what turns 24 discrete poses
  // into continuous rotation.
  draw(pos) {
    if (!this.ctx) return;

    const base = Math.floor(pos);
    const frac = this.blend ? pos - base : 0;
    const img = this.frameAt(base) || this.images.find(Boolean);
    if (!img) return;

    const { width: cw, height: ch } = this.canvas;
    this.ctx.clearRect(0, 0, cw, ch);
    this.paint(img, 1);

    if (frac > 0.001) {
      const next = this.frameAt(base + 1);
      if (next) this.paint(next, frac);
    }
  }

  paint(img, alpha) {
    const { width: cw, height: ch } = this.canvas;
    const scale = Math.min(cw / img.naturalWidth, ch / img.naturalHeight) * this.zoom;
    const w = img.naturalWidth * scale;
    const h = img.naturalHeight * scale;
    this.ctx.globalAlpha = alpha;
    this.ctx.drawImage(img, (cw - w) / 2, (ch - h) / 2, w, h);
    this.ctx.globalAlpha = 1;
  }

  destroy() {
    this.destroyed = true;
    removeEventListener('scroll', this.onScroll);
    removeEventListener('resize', this.onResize);
    if (this.observer) this.observer.disconnect();
    if (this.rafAuto) cancelAnimationFrame(this.rafAuto);
    if (this.rafVideo) cancelAnimationFrame(this.rafVideo);
    clearTimeout(this.swapTimer);
    this.clearWatchdog();
    if (this.video) {
      this.video.pause();
      this.video.removeAttribute('src');
      this.video.load();
    }
  }
}

const registry = new WeakMap();

const mount = (root = document) => {
  root.querySelectorAll('[data-flrc-spin]').forEach((track) => {
    if (registry.has(track)) return;
    registry.set(track, new ProductSpin(track));
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => mount());
} else {
  mount();
}

document.addEventListener('shopify:section:load', (event) => mount(event.target));
document.addEventListener('shopify:section:unload', (event) => {
  event.target.querySelectorAll('[data-flrc-spin]').forEach((track) => {
    const spin = registry.get(track);
    if (spin) spin.destroy();
    registry.delete(track);
  });
});

if (MOBILE.addEventListener) {
  MOBILE.addEventListener('change', () => {
    document.querySelectorAll('[data-flrc-spin]').forEach((track) => {
      const spin = registry.get(track);
      if (spin) spin.onResize();
    });
  });
}
